import { useEffect, useState } from "react";
import { navItems } from "@/mocks/lustCaution";

export default function SiteNav() {
  const [scrolled, setScrolled] = useState(false);
  const [active, setActive] = useState("overview");
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const sections = navItems
      .map((item) => document.getElementById(item.id))
      .filter((el): el is HTMLElement => Boolean(el));

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(entry.target.id);
        });
      },
      { rootMargin: "-45% 0px -50% 0px", threshold: 0 }
    );

    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, []);

  const handleJump = (id: string) => {
    setMenuOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled
          ? "bg-background-50/95 backdrop-blur border-b border-background-200"
          : "bg-transparent"
      }`}
    >
      <div className="flex items-center justify-between gap-4 px-4 md:px-8 h-16">
        <a
          href="#top"
          onClick={(e) => {
            e.preventDefault();
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
          className="flex items-center gap-2 cursor-pointer shrink-0"
        >
          <span className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-500 text-background-50">
            <i className="ri-film-line text-base" aria-hidden="true"></i>
          </span>
          <span
            className={`font-heading text-base md:text-lg font-semibold tracking-wide transition-colors ${
              scrolled ? "text-foreground-950" : "text-background-50"
            }`}
          >
            色，戒 · 版本档案
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-1">
          {navItems.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => handleJump(item.id)}
              className={`px-3 py-2 rounded-md text-sm font-label transition-colors cursor-pointer whitespace-nowrap ${
                active === item.id
                  ? scrolled
                    ? "bg-primary-100 text-primary-700"
                    : "bg-background-50/20 text-background-50"
                  : scrolled
                    ? "text-foreground-600 hover:text-primary-600 hover:bg-background-100"
                    : "text-background-100 hover:text-background-50"
              }`}
            >
              {item.label}
            </button>
          ))}
        </nav>

        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          aria-label="打开导航菜单"
          aria-expanded={menuOpen}
          className={`lg:hidden w-10 h-10 flex items-center justify-center rounded-md cursor-pointer transition-colors ${
            scrolled ? "text-foreground-950" : "text-background-50"
          }`}
        >
          <i className={menuOpen ? "ri-close-line text-2xl" : "ri-menu-line text-2xl"} aria-hidden="true"></i>
        </button>
      </div>

      {menuOpen && (
        <div className="lg:hidden bg-background-50 border-b border-background-200 px-4 pb-4">
          <div className="grid grid-cols-2 gap-2 pt-2">
            {navItems.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => handleJump(item.id)}
                className={`px-3 py-3 rounded-md text-sm font-label text-left cursor-pointer transition-colors ${
                  active === item.id
                    ? "bg-primary-100 text-primary-700"
                    : "bg-background-100 text-foreground-700"
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}